import type { CoinbaseEngUsersAccountManagementApiApiWorkspaceMember } from '@cbhq/identity-api-hooks/account-management/types';

import { ACCOUNT_MANAGEMENT_LE_ADMIN, WORKSPACE_ORG_ADMIN } from '../../../constants/roles';

/**
 * The relevant slice of a GET /organization/v1/workspace/members entry.
 * The API always returns the current user first, so callers pass `members[0]`.
 */
type WorkspaceMemberAccess = Pick<
  CoinbaseEngUsersAccountManagementApiApiWorkspaceMember,
  'legalEntityRoles' | 'roles'
>;

/**
 * Returns true if the current user may manage members on `legalEntityId`.
 * The trays list every entity the user can see (ACM viewer or admin), but only
 * an admin on that specific entity — or a workspace_org_admin, which spans the
 * whole org — may change roles there.
 */
export function canManageEntity(
  currentUser: WorkspaceMemberAccess | undefined,
  legalEntityId: string,
): boolean {
  if (currentUser?.roles?.includes(WORKSPACE_ORG_ADMIN)) {
    return true;
  }

  return (currentUser?.legalEntityRoles ?? []).some(
    (ler) =>
      ler.legalEntityId === legalEntityId &&
      (ler.roles ?? []).includes(ACCOUNT_MANAGEMENT_LE_ADMIN),
  );
}

/**
 * Returns true if the current user may manage members (invite or edit roles).
 * Invite and edit share the exact same permission. Requires either:
 * - account_management/legal_entity:admin on at least one legal entity
 *   (lives in `legalEntityRoles[].roles`), or
 * - workspace_org_admin (org-level, lives in the top-level `roles`).
 */
export function canManageMembers(currentUser: WorkspaceMemberAccess | undefined): boolean {
  const legalEntityRoles = currentUser?.legalEntityRoles?.flatMap((ler) => ler.roles ?? []) ?? [];
  const orgRoles = currentUser?.roles ?? [];

  return (
    legalEntityRoles.includes(ACCOUNT_MANAGEMENT_LE_ADMIN) || orgRoles.includes(WORKSPACE_ORG_ADMIN)
  );
}

/**
 * Returns true if the current user may remove a member from the organization.
 * Stricter than managing members: requires the org-level workspace_org_admin role
 * (lives in `members[0].roles`).
 */
export function canRemoveMembers(currentUser: WorkspaceMemberAccess | undefined): boolean {
  return currentUser?.roles?.includes(WORKSPACE_ORG_ADMIN) ?? false;
}
